// src/components/ResourceFreshnessTag.jsx
// Resource Freshness Indicator.
//
// Shows users exactly how stale resource information is, with confidence
// context. Resource reports (gas, shelter, pharmacy, hospital, charging)
// change far faster than incident reports — a gas station "open" report
// from 2 hours ago during a hurricane is likely wrong.
//
// Display format:
//   🟢 Updated 18 min ago · Confidence 82%          (fresh)
//   🟡 Updated 1h 12m ago · Confidence 54% · Verify  (aging)
//   🔴 Updated 3h ago · Confidence 22% · Likely stale (stale)
//
// Used on:
//   Gas station cards    (GasPage)
//   Shelter cards        (SheltersPage / GuidesPage)
//   Hospital/pharmacy    (ContactsPage, HospitalsPage)
//   Charging stations    (ConnectivityPage)
//   Supply distribution  (ResourcesPage)
//
// Props:
//   type        — resource type key (gas_station | shelter_availability |
//                 medical_resource | charging_station | supply_distribution)
//   tsMs        — Unix ms timestamp of the report
//   confidence  — 0–1 confidence score from the intelligence pipeline
//   compact     — boolean: show shorter version (default false)
//   onVerify    — optional callback when user taps "I can verify this"

import { T } from '@components/theme';

// ── Freshness thresholds per resource type ────────────────────────
// Mirrors decay.js thresholds but surfaces them to the user.
// Values in milliseconds.
const RESOURCE_THRESHOLDS = {
  gas_station: {
    aging:  30 * 60_000,    // 30min — warn
    stale:  90 * 60_000,    // 90min — stale
    critical: 3 * 60 * 60_000, // 3h — critical
    label:  'Gas Station Status',
    icon:   '⛽',
    tip:    'Gas availability changes rapidly during storms.',
  },
  shelter_availability: {
    aging:  20 * 60_000,
    stale:  60 * 60_000,    // 1h
    critical: 2 * 60 * 60_000,
    label:  'Shelter Capacity',
    icon:   '🏫',
    tip:    'Shelter capacity fills quickly during evacuations.',
  },
  medical_resource: {
    aging:  60 * 60_000,    // 1h
    stale:  3 * 60 * 60_000, // 3h
    critical: 6 * 60 * 60_000,
    label:  'Medical Facility Status',
    icon:   '🏥',
    tip:    'Hospital and pharmacy status changes with power and staffing.',
  },
  charging_station: {
    aging:  45 * 60_000,
    stale:  2 * 60 * 60_000,
    critical: 4 * 60 * 60_000,
    label:  'Charging Station',
    icon:   '🔋',
    tip:    'Charging availability depends on grid restoration.',
  },
  supply_distribution: {
    aging:  20 * 60_000,
    stale:  60 * 60_000,
    critical: 2 * 60 * 60_000,
    label:  'Supply Distribution',
    icon:   '📦',
    tip:    'Water and ice distribution depletes quickly.',
  },
  // Fallback for plain 'gas', 'shelter' type names (legacy)
  gas: {
    aging:  30 * 60_000, stale: 90 * 60_000, critical: 3 * 60 * 60_000,
    label: 'Gas Station', icon: '⛽', tip: 'Gas availability changes rapidly.',
  },
  shelter: {
    aging: 20 * 60_000, stale: 60 * 60_000, critical: 2 * 60 * 60_000,
    label: 'Shelter', icon: '🏫', tip: 'Capacity fills quickly.',
  },
};

function _getFreshnessLevel(type, tsMs) {
  const cfg    = RESOURCE_THRESHOLDS[type];
  if (!cfg || !tsMs) return null;
  const ageMs  = Date.now() - tsMs;
  if (ageMs >= cfg.critical) return 'critical';
  if (ageMs >= cfg.stale)    return 'stale';
  if (ageMs >= cfg.aging)    return 'aging';
  return 'fresh';
}

function _formatAge(tsMs) {
  const ageMs  = Date.now() - tsMs;
  const ageMin = Math.round(ageMs / 60_000);
  if (ageMin < 1)   return 'just now';
  if (ageMin < 60)  return `${ageMin} min ago`;
  const h = Math.floor(ageMin / 60);
  const m = ageMin % 60;
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
}

// ── Inline tag — compact version for cards ────────────────────────
export function ResourceFreshnessTag({ type, tsMs, confidence, compact = false }) {
  if (!tsMs) return null;

  const level  = _getFreshnessLevel(type, tsMs);
  if (!level)  return null;

  const ageLabel  = _formatAge(tsMs);
  const confPct   = confidence != null ? Math.round(confidence * 100) : null;

  const COLOR = {
    fresh:    '#059669',
    aging:    '#d97706',
    stale:    '#9ca3af',
    critical: '#dc2626',
  };
  const DOT = {
    fresh: '🟢', aging: '🟡', stale: '🔴', critical: '🔴',
  };
  const SUFFIX = {
    fresh:    '',
    aging:    ' · Verify',
    stale:    ' · Likely stale',
    critical: ' · Probably outdated',
  };

  const color = COLOR[level];
  const bg    = `${color}10`;
  const bdr   = `${color}25`;

  if (compact) {
    return (
      <span style={{
        display:     'inline-flex', alignItems: 'center', gap: 3,
        fontSize:    8, fontWeight: 700,
        padding:     '1px 5px', borderRadius: 4,
        background:  bg, border: `1px solid ${bdr}`, color,
        flexShrink:  0, whiteSpace: 'nowrap',
      }}>
        {DOT[level]} {ageLabel}
        {confPct != null && ` · ${confPct}%`}
        {SUFFIX[level]}
      </span>
    );
  }

  return (
    <div style={{
      display:       'flex', alignItems: 'flex-start', gap: 6,
      padding:       '7px 10px', borderRadius: 8, marginTop: 4,
      background:    bg, border: `1px solid ${bdr}`,
    }}>
      <span style={{ fontSize: 14, flexShrink: 0 }}>{DOT[level]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize:   10, fontWeight: 700, color, lineHeight: 1.4,
        }}>
          Updated {ageLabel}
          {confPct != null && ` · Confidence ${confPct}%`}
          {SUFFIX[level]}
        </div>
        {level !== 'fresh' && (
          <div style={{
            fontSize:  9, color: '#6b7280', marginTop: 2, lineHeight: 1.4,
          }}>
            {RESOURCE_THRESHOLDS[type]?.tip}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Full panel — expanded version for detail view ─────────────────
export function ResourceFreshnessPanel({ type, tsMs, confidence, onVerify }) {
  if (!tsMs) return null;

  const level   = _getFreshnessLevel(type, tsMs);
  const cfg     = RESOURCE_THRESHOLDS[type];
  if (!cfg)     return null;

  const ageLabel = _formatAge(tsMs);
  const confPct  = confidence != null ? Math.round(confidence * 100) : null;

  const LEVEL_CONFIG = {
    fresh: {
      color: '#059669', bg: 'rgba(5,150,105,0.08)', bdr: 'rgba(5,150,105,0.2)',
      headline: `${cfg.icon} ${cfg.label} — Recently Updated`,
      body: `This information was last verified ${ageLabel}.`,
      showVerify: false,
    },
    aging: {
      color: '#d97706', bg: 'rgba(217,119,6,0.08)', bdr: 'rgba(217,119,6,0.25)',
      headline: `⚠ ${cfg.label} — May be Changing`,
      body: `Updated ${ageLabel}. ${cfg.tip} Confirm before relying on this.`,
      showVerify: true,
    },
    stale: {
      color: '#9ca3af', bg: 'rgba(107,114,128,0.08)', bdr: 'rgba(107,114,128,0.2)',
      headline: `⏱ ${cfg.label} — Likely Stale`,
      body: `Updated ${ageLabel}. Conditions may have changed significantly.`,
      showVerify: true,
    },
    critical: {
      color: '#dc2626', bg: 'rgba(220,38,38,0.08)', bdr: 'rgba(220,38,38,0.2)',
      headline: `🔴 ${cfg.label} — Probably Outdated`,
      body: `Updated ${ageLabel}. This information is too old to rely on without verification.`,
      showVerify: true,
    },
  };

  const cfg2 = LEVEL_CONFIG[level ?? 'fresh'];

  return (
    <div style={{
      padding:    '10px 12px', borderRadius: 9,
      background:  cfg2.bg, border: `1px solid ${cfg2.bdr}`,
      marginTop:   6,
      fontFamily:  T.font,
    }}>
      <div style={{
        fontSize:   11, fontWeight: 800, color: cfg2.color, marginBottom: 4,
      }}>
        {cfg2.headline}
      </div>
      <div style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.5 }}>
        {cfg2.body}
      </div>

      {/* Confidence bar */}
      {confPct != null && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280' }}>
              Confidence
            </span>
            <span style={{ fontSize: 9, fontWeight: 700, color: cfg2.color }}>
              {confPct}%
            </span>
          </div>
          <div style={{
            height: 4, background: 'rgba(255,255,255,0.07)',
            borderRadius: 2, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width:  `${confPct}%`,
              background: cfg2.color,
              transition: 'width 0.4s',
            }} />
          </div>
        </div>
      )}

      {/* Verify CTA */}
      {cfg2.showVerify && onVerify && (
        <button
          onClick={onVerify}
          style={{
            marginTop:    8, padding: '6px 12px', borderRadius: 7,
            border:      `1px solid ${cfg2.bdr}`,
            background:   'transparent', color: cfg2.color,
            fontSize:     10, fontWeight: 700, cursor: 'pointer',
            fontFamily:   T.font,
          }}
        >
          📍 I can verify this — update the status
        </button>
      )}
    </div>
  );
}
